import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { Check, DatePicker, DateRangeSelector, SelectSingle, FormikControl, checkResponses } from '../../../../components/form';
import { formatDateApi, getDateObject, convertApiToDate, getymd, exportToCSV } from '../../../../components/functions';
import { Socket } from '../../../../components/ws';
import { generateStandard } from './helpers';
import { Table } from '../../../../components/table';

import {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchStudentsAll,
    fetchAppointmentsDaterangeStudent,
    fetchLearningPlanItemsStudentDaterangeCompleted,
    fetchLessonsAll,
    updateStudentAttributes
} from '../../../../actions';
import {
    fetchGraphicND
} from '../../../../actions-nd';

// Portal detection
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');
const pageTitle = 'Report Generator / Progress Reports';

const coerceLinkValue = (rawLink) => {
    if (!rawLink) return '';
    if (typeof rawLink === 'string') return rawLink;
    if (typeof rawLink === 'object') {
        if (typeof rawLink.url === 'string') return rawLink.url;
        if (typeof rawLink.link === 'string') return rawLink.link;
        if (typeof rawLink.href === 'string') return rawLink.href;
        if (typeof rawLink.value === 'string') return rawLink.value;
        return '';
    }
    return String(rawLink);
};

const sanitizeReportLink = (rawLink) => {
    const linkValue = coerceLinkValue(rawLink).trim();
    if (!linkValue || linkValue.toLowerCase() === '[object object]') return '';
    let link = linkValue;
    if (!/^https?:\/\//i.test(link) && !/^mailto:/i.test(link)) {
        link = `https://${link}`;
    }
    return link;
};

const normalizeReportNotes = (rawNotes) => {
    if (!rawNotes) return '';
    if (typeof rawNotes === 'string') {
        const trimmed = rawNotes.trim();
        if (!trimmed) return '';
        if (trimmed.toLowerCase() === '[object object]') return '';
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                return normalizeReportNotes(parsed);
            } catch (error) {
                return '';
            }
        }
        return trimmed;
    }
    if (Array.isArray(rawNotes)) {
        return rawNotes.map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
                if (typeof item.insert === 'string') return item.insert;
                if (typeof item.text === 'string') return item.text;
            }
            return '';
        }).join('').trim();
    }
    if (typeof rawNotes === 'object') {
        if (Array.isArray(rawNotes.ops)) {
            return rawNotes.ops.map((op) => typeof op.insert === 'string' ? op.insert : '').join('').trim();
        }
        if (rawNotes.text) return String(rawNotes.text).trim();
        if (rawNotes.value) return String(rawNotes.value).trim();
        if (rawNotes.notes) return String(rawNotes.notes).trim();
        if (rawNotes.insert && typeof rawNotes.insert === 'string') return rawNotes.insert.trim();
        if (rawNotes.delta && Array.isArray(rawNotes.delta.ops)) {
            return rawNotes.delta.ops.map((op) => typeof op.insert === 'string' ? op.insert : '').join('').trim();
        }
        try {
            return JSON.stringify(rawNotes);
        } catch (error) {
            console.warn('[PROGRESS REPORTS] Unable to stringify notes object:', error);
            return '';
        }
    }
    return String(rawNotes).trim();
};

const reportTypeOptions = [
    { value: 'standard', label: 'Standard PR' }
];

const start = new Date();
start.setMonth(start.getMonth() - 1);
const startApi = formatDateApi(start);
const endApi = formatDateApi(new Date());

function ReportGenerator(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [drsValid, setDrsValid] = useState(true);
    const [apiError, setApiError] = useState(false);

    // Active Tab
    const [activeTab, setActiveTab] = useState('generator');

    // DATA
    const [students, setStudents] = useState([]);
    const [studentOptions, setStudentOptions] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [logoData, setLogoData] = useState(null);
    const [centers, setCenters] = useState([{ value: -1, label: 'Loading centers...' }]);

    // Progress Reports - ONLY store necessary fields, not full student object
    const [progressReports, setProgressReports] = useState([]);
    const [filteredProgressReports, setFilteredProgressReports] = useState([]);
    const [filterQuery, setFilterQuery] = useState('');
    const [showSentOnly, setShowSentOnly] = useState(false);
    const [showNotSentOnly, setShowNotSentOnly] = useState(false);
    const [showWithLinksOnly, setShowWithLinksOnly] = useState(false);

    // Edit Modal State
    const [editingReport, setEditingReport] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const { fetchAppointmentsDaterangeStudent, fetchMpCentersAll, fetchRpCentersAll, fetchAdminUsersAll, fetchStudentsAll,
        fetchLearningPlanItemsStudentDaterangeCompleted, fetchLessonsAll, updateStudentAttributes } = props;

    // Build progress reports - portal-aware
    const buildProgressReports = useCallback((studentsData) => {
        return studentsData
            .filter(s => {
                if (isReadingPlex) {
                    return parseInt(s.is_rp_student) === 1 && parseInt(s.rp_active) === 1 && parseInt(s.is_lead) === 0;
                } else {
                    return parseInt(s.is_mp_student) === 1 && parseInt(s.mp_active) === 1 && parseInt(s.is_lead) === 0;
                }
            })
            .map(s => {
                let notes = normalizeReportNotes(isReadingPlex ? s.rp_monthly_report_text : s.mp_monthly_report_text);
                if (notes && notes.toLowerCase().includes('[object object]')) {
                    notes = '';
                }
                const link = sanitizeReportLink(isReadingPlex ? s.rp_monthly_report_link : s.mp_monthly_report_link);
                return {
                    id: s.user_id,
                    user_id: s.user_id,
                    studentName: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                    reportLink: link,
                    reportNotes: notes,
                    reportSent: parseInt(isReadingPlex ? (s.rp_monthly_report_sent || 0) : (s.mp_monthly_report_sent || 0))
                };
            });
    }, []);

    // Filter progress reports
    useEffect(() => {
        let filtered = [...progressReports];

        if (filterQuery && filterQuery.trim()) {
            const lowerQuery = filterQuery.toLowerCase();
            filtered = filtered.filter(r =>
                (r.studentName || '').toLowerCase().includes(lowerQuery) ||
                (r.reportNotes || '').toLowerCase().includes(lowerQuery) ||
                (r.reportLink || '').toLowerCase().includes(lowerQuery)
            );
        }

        if (showSentOnly) {
            filtered = filtered.filter(r => r.reportSent === 1);
        }

        if (showNotSentOnly) {
            filtered = filtered.filter(r => r.reportSent !== 1);
        }

        if (showWithLinksOnly) {
            filtered = filtered.filter(r => r.reportLink && r.reportLink.trim());
        }

        setFilteredProgressReports(filtered);
    }, [progressReports, filterQuery, showSentOnly, showNotSentOnly, showWithLinksOnly]);

    const handleSubmit = useCallback((values, actions) => {
        (async function submit() {
            if (loading || !drsValid || !formRef.current?.values) return;
            if (mounted.current) setLoading(true);

            const { startDate, endDate, selectedStudent, selectedReportType } = values;
            const { setStatus, setSubmitting } = actions;

            if (selectedStudent.value === -1) {
                setStatus('Please select a valid student');
                if (mounted.current) {
                    setSubmitting(false);
                    setLoading(false);
                }
                return;
            } else if (selectedReportType.value === -1) {
                setStatus('Please select a report type');
                if (mounted.current) {
                    setSubmitting(false);
                    setLoading(false);
                }
                return;
            }

            const instructorMap = {};
            instructors.forEach(i => instructorMap[i.id] = `${i.first_name} ${i.last_name}`);

            const centerMap = {};
            centers.forEach(c => centerMap[parseInt(c.id)] = c.name);

            const startDateObj = getDateObject(convertApiToDate(startDate));
            const endDateObj = getDateObject(convertApiToDate(endDate));
            const appointmentsRes = await fetchAppointmentsDaterangeStudent({
                startDate: startDateObj.raw,
                endDate: endDateObj.raw,
                student: selectedStudent.value
            });

            // For ReadingPlex, learning plan items and lessons don't exist - handle gracefully
            let learningPlanItemsRes = null;
            let lessonsRes = null;
            if (!isReadingPlex) {
                learningPlanItemsRes = await fetchLearningPlanItemsStudentDaterangeCompleted({
                    startDate: startDate,
                    endDate: endDate,
                    student: selectedStudent.value
                });
                lessonsRes = await fetchLessonsAll();
            }

            // Only check appointments API for errors - learning plan items/lessons are optional for ReadingPlex
            const isApiError = checkResponses(appointmentsRes);
            if (isApiError) {
                if (mounted.current) {
                    setApiError('Error fetching data from the server. Please try again later.');
                    setLoading(false);
                }
                return;
            } else setApiError(false);

            const newAppointmentsData = appointmentsRes.data || [];
            // For ReadingPlex, these will be empty arrays since the endpoints don't exist
            const newLearningPlanItems = (learningPlanItemsRes && learningPlanItemsRes.data) ? learningPlanItemsRes.data : [];
            const newLessons = (lessonsRes && lessonsRes.data) ? lessonsRes.data : [];

            const newAppointments = newAppointmentsData.appointments || [];
            const newAssignments = newAppointmentsData.assignments || [];
            const appointmentAssignmentMap = {};
            newAssignments.forEach(a => {
                const appointmentId = parseInt(a.appointment_id);
                if (!appointmentAssignmentMap[appointmentId]) appointmentAssignmentMap[appointmentId] = [];
                appointmentAssignmentMap[appointmentId].push(a);
            });

            const lessonMap = {};
            newLessons.forEach(l => {
                lessonMap[parseInt(l.id)] = `${l.lesson_id}: ${l.name}`;
            });
            newLearningPlanItems.forEach(lpi => {
                lpi.lessonName = lessonMap[parseInt(lpi.lesson_id)] || `Unknown lesson (${lpi.lesson_id})`;
            });
            const dateToLpiMap = {};
            newLearningPlanItems.forEach(lpi => {
                const [year, month, date] = getymd(new Date(lpi.date_completed));
                const dateKey = `${year}-${month}-${date}`;
                if (!dateToLpiMap[dateKey]) dateToLpiMap[dateKey] = [];
                dateToLpiMap[dateKey].push(lpi.lessonName);
            });

            newAppointments.forEach(a => {
                a.centerName = centerMap[parseInt(a.center)] || `Unknown center (ID: ${a.center})`;
                a.assignedInstructors = appointmentAssignmentMap[parseInt(a.id)] || [];
                a.assignedInstructorNames = '';
                a.assignedInstructors.forEach(ai => {
                    const instructorName = (instructorMap[ai.instructor] || `Unknown admin user (ID: ${ai.instructor})`) + ', ';
                    a.assignedInstructorNames += (instructorName);
                });
                const instructorNames = a.assignedInstructorNames;
                if (instructorNames) a.assignedInstructorNames = instructorNames.slice(0, instructorNames.length - 2);

                const aptDateTime = new Date(a.date_time);
                const [year, month, date] = getymd(aptDateTime);
                const dateKey = `${year}-${month}-${date}`;
                a.lessonsCompleted = dateToLpiMap[dateKey] || [];
            });

            if (selectedReportType.value === 'standard') {
                if (!newAppointmentsData.appointments?.length) {
                    setStatus('No appointments were found.');
                    if (mounted.current) {
                        setSubmitting(false);
                        setLoading(false);
                    }
                    return;
                }
                try {
                    await generateStandard(values, newAppointments, logoData,
                        values.quickDownload, values.quickDownloadDate);
                } catch (error) {
                    console.error('[REPORT GENERATOR] Error generating report:', error);
                    if (mounted.current) {
                        setStatus(`Error generating report: ${error.message || 'Unknown error'}`);
                        setSubmitting(false);
                        setLoading(false);
                    }
                    return;
                }
            }

            if (mounted.current) {
                setSubmitting(false);
                setLoading(false);
            }
        })();
    }, [loading, drsValid, instructors, centers, logoData, fetchAppointmentsDaterangeStudent,
        fetchLearningPlanItemsStudentDaterangeCompleted, fetchLessonsAll]);

    // Handle edit progress report
    const handleEditReport = useCallback((report) => {
        const normalizedLink = sanitizeReportLink(report.reportLink);
        const normalizedNotes = normalizeReportNotes(report.reportNotes);
        setEditingReport({
            user_id: report.user_id,
            studentName: report.studentName,
            reportLink: normalizedLink,
            reportNotes: normalizedNotes,
            reportSent: report.reportSent === 1
        });
        setShowEditModal(true);
    }, []);

    // Handle save progress report - portal-aware
    const handleSaveReport = useCallback(async (values) => {
        if (!editingReport) return;

        setLoading(true);
        try {
            const studentData = students.find(s => s.user_id === editingReport.user_id);
            if (!studentData) {
                setApiError('Student not found');
                setLoading(false);
                return;
            }

            // Build update params - only include the monthly report fields
            // The backend should handle partial updates
            const normalizedLink = sanitizeReportLink(values.reportLink);
            const normalizedNotes = normalizeReportNotes(values.reportNotes);
            const updateParams = {
                userId: studentData.user_id,
                // Include portal-specific monthly report fields
                ...(isReadingPlex ? {
                    rpMonthlyReportLink: normalizedLink,
                    rpMonthlyReportText: normalizedNotes,
                    rpMonthlyReportSent: values.reportSent ? 1 : 0
                } : {
                    monthlyReportLink: normalizedLink,
                    monthlyReportText: normalizedNotes,
                    monthlyReportSent: values.reportSent ? 1 : 0
                })
            };

            const res = await updateStudentAttributes(updateParams);
            if (res && res.data) {
                // Update students state
                const updatedStudents = students.map(s => {
                    if (s.user_id === editingReport.user_id) {
                        if (isReadingPlex) {
                            return {
                                ...s,
                                rp_monthly_report_link: normalizedLink,
                                rp_monthly_report_text: normalizedNotes,
                                rp_monthly_report_sent: values.reportSent ? 1 : 0
                            };
                        } else {
                            return {
                                ...s,
                                mp_monthly_report_link: normalizedLink,
                                mp_monthly_report_text: normalizedNotes,
                                mp_monthly_report_sent: values.reportSent ? 1 : 0
                            };
                        }
                    }
                    return s;
                });
                setStudents(updatedStudents);
                setProgressReports(buildProgressReports(updatedStudents));
                setShowEditModal(false);
                setEditingReport(null);
                setApiError(false);
            } else {
                setApiError('Failed to update progress report');
            }
        } catch (error) {
            console.error('Error updating progress report:', error);
            setApiError('Error updating progress report');
        }
        setLoading(false);
    }, [editingReport, students, updateStudentAttributes, buildProgressReports]);

    // Handle delete (clear) progress report link
    const handleClearReport = useCallback(async (report) => {
        if (!report || !report.user_id) return;
        if (!window.confirm(`Clear the progress report link for ${report.studentName}?`)) {
            return;
        }

        setLoading(true);
        try {
            const studentData = students.find(s => s.user_id === report.user_id);
            if (!studentData) {
                setApiError('Student not found');
                setLoading(false);
                return;
            }

            const updateParams = {
                userId: studentData.user_id,
                ...(isReadingPlex ? {
                    rpMonthlyReportLink: '',
                    rpMonthlyReportText: '',
                    rpMonthlyReportSent: 0
                } : {
                    monthlyReportLink: '',
                    monthlyReportText: '',
                    monthlyReportSent: 0
                })
            };

            const res = await updateStudentAttributes(updateParams);
            if (res && res.data) {
                const updatedStudents = students.map(s => {
                    if (s.user_id === report.user_id) {
                        if (isReadingPlex) {
                            return {
                                ...s,
                                rp_monthly_report_link: '',
                                rp_monthly_report_text: '',
                                rp_monthly_report_sent: 0
                            };
                        } else {
                            return {
                                ...s,
                                mp_monthly_report_link: '',
                                mp_monthly_report_text: '',
                                mp_monthly_report_sent: 0
                            };
                        }
                    }
                    return s;
                });
                setStudents(updatedStudents);
                setProgressReports(buildProgressReports(updatedStudents));
                setApiError(false);
            } else {
                setApiError('Failed to clear progress report');
            }
        } catch (error) {
            console.error('Error clearing progress report:', error);
            setApiError('Error clearing progress report');
        }
        setLoading(false);
    }, [students, updateStudentAttributes, buildProgressReports]);

    // Export to CSV
    const handleExportCSV = useCallback(() => {
        const dataToExport = filteredProgressReports.map(r => ({
            'Student Name': r.studentName || '',
            'Report Link': r.reportLink || '',
            'Report Notes': r.reportNotes || '',
            'Sent': r.reportSent === 1 ? 'Yes' : 'No'
        }));
        exportToCSV(dataToExport, `monthly_progress_reports_${formatDateApi(new Date())}`);
    }, [filteredProgressReports]);

    // Table columns
    const defaultSorted = { dataField: 'studentName', order: 'asc' };

    const columns = [
        {
            dataField: 'studentName',
            text: 'Student',
            sort: true
        },
        {
            dataField: 'reportLink',
            text: 'Report Link',
            sort: false,
            formatter: (row) => {
                const link = sanitizeReportLink(row.reportLink);
                if (link) {
                    return (
                        <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-mpTeal hover:underline"
                        >
                            Open Report
                        </a>
                    );
                }
                return <span className="text-gray-400 italic">No link</span>;
            }
        },
        {
            dataField: 'reportNotes',
            text: 'Notes',
            sort: false,
            formatter: (row) => {
                const text = normalizeReportNotes(row.reportNotes);
                if (!text) return <span className="text-gray-400 italic">-</span>;
                return <span>{text.length > 40 ? `${text.substring(0, 40)}...` : text}</span>;
            }
        },
        {
            dataField: 'reportSent',
            text: 'Sent',
            sort: true,
            formatter: (row) => {
                const sent = parseInt(row.reportSent || 0) === 1;
                return (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${sent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {sent ? 'Sent' : 'Not Sent'}
                    </span>
                );
            }
        },
        {
            dataField: 'user_id',
            text: 'Actions',
            sort: false,
            formatter: (row) => {
                const normalizedLink = sanitizeReportLink(row?.reportLink);
                const hasLink = !!normalizedLink;
                return (
                    <div className="flex flex-row gap-x-2">
                        <TooltipWrapper tooltipText="Edit">
                            <Button
                                color="lte-mpLBlue"
                                variant="icon"
                                onClick={() => handleEditReport(row)}
                            >
                                <SVGIcon fileName="pencil" />
                            </Button>
                        </TooltipWrapper>
                        {hasLink && (
                            <TooltipWrapper tooltipText="Open">
                                <Button
                                    color="lte-mpEGreen"
                                    variant="icon"
                                    onClick={() => window.open(normalizedLink, '_blank', 'noopener')}
                                >
                                    <SVGIcon fileName="external-link" />
                                </Button>
                            </TooltipWrapper>
                        )}
                        {hasLink && (
                            <TooltipWrapper tooltipText="Clear">
                                <Button
                                    color="lte-mpLRed"
                                    variant="icon"
                                    onClick={() => handleClearReport(row)}
                                >
                                    <SVGIcon fileName="trash" />
                                </Button>
                            </TooltipWrapper>
                        )}
                    </div>
                );
            }
        }
    ];

    useEffect(() => {
        async function init() {
            setLoading(true);

            // Use portal-specific center fetch
            const centersRes = isReadingPlex ? await fetchRpCentersAll() : await fetchMpCentersAll();
            const studentsRes = await fetchStudentsAll();
            const instructorsRes = await fetchAdminUsersAll();
            const logoRes = await fetchGraphicND({ fileName: isReadingPlex ? 'RP Long Light@4x.png' : 'MP Long Light@4x.png' });
            const isApiError = checkResponses(centersRes, studentsRes, instructorsRes);
            if (isApiError) {
                if (mounted.current) {
                    setApiError('Error fetching data from the server. Please refresh the page or try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
                return;
            }

            const newCenters = centersRes.data || [];
            const newStudents = studentsRes.data || [];
            const newInstructors = instructorsRes.data || [];

            // Filter students based on portal
            const newStudentOptions = newStudents.filter(s => {
                if (isReadingPlex) {
                    return parseInt(s.rp_active) === 1 && parseInt(s.is_rp_student) === 1 && parseInt(s.is_lead) === 0;
                } else {
                    return parseInt(s.mp_active) === 1 && parseInt(s.is_mp_student) === 1 && parseInt(s.is_lead) === 0;
                }
            }).map(s => ({
                value: s.user_id,
                label: `${s.first_name} ${s.last_name}`,
                obj: s
            }));

            // Build progress reports using shared helper
            const reports = buildProgressReports(newStudents);

            if (mounted.current) {
                setStudents(newStudents);
                setStudentOptions(newStudentOptions);
                setCenters(newCenters);
                setInstructors(newInstructors);
                setLogoData(logoRes.data || null);
                setProgressReports(reports);
                setFilteredProgressReports(reports);
                setLoading(false);
                setHasLoaded(true);
            }
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}

            {/* Edit Modal */}
            {showEditModal && editingReport && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
                        <h3 className="text-xl font-semibold mb-4">
                            Edit Progress Report - {editingReport.studentName}
                        </h3>
                        <Formik
                            initialValues={{
                                reportLink: editingReport.reportLink || '',
                                reportNotes: editingReport.reportNotes || '',
                                reportSent: editingReport.reportSent === true || editingReport.reportSent === 1
                            }}
                            onSubmit={handleSaveReport}
                        >
                            {formik => (
                                <div className="space-y-4">
                                    <div>
                                        <h4>Google Drive Report Link</h4>
                                        <FormikControl
                                            id="edit-reportLink"
                                            name="reportLink"
                                            placeholder="https://docs.google.com/..."
                                            value={formik.values.reportLink}
                                            onChange={formik.handleChange}
                                        />
                                    </div>
                                    <div>
                                        <h4>Report Notes</h4>
                                        <FormikControl
                                            id="edit-reportNotes"
                                            name="reportNotes"
                                            as="textarea"
                                            placeholder="Notes about this report..."
                                            value={formik.values.reportNotes}
                                            onChange={formik.handleChange}
                                        />
                                    </div>
                                    <div>
                                        <Check
                                            id="edit-reportSent"
                                            name="reportSent"
                                            label="Report Sent to Parent"
                                            color="mpTeal"
                                            checked={formik.values.reportSent}
                                            onChange={formik.handleChange}
                                        />
                                    </div>
                                    <div className="flex justify-end gap-x-3 pt-4">
                                        <Button
                                            color="lte-mpLRed"
                                            onClick={() => {
                                                setShowEditModal(false);
                                                setEditingReport(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            color="lte-mpTeal"
                                            onClick={formik.handleSubmit}
                                        >
                                            Save Changes
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Formik>
                    </div>
                </div>
            )}

            {hasLoaded &&
                <div className="card">
                    {/* Tab Navigation */}
                    <div className="flex border-b mb-4">
                        <button
                            className={`px-4 py-2 font-medium ${activeTab === 'generator'
                                ? 'text-mpTeal border-b-2 border-mpTeal'
                                : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('generator')}
                        >
                            Report Generator
                        </button>
                        <button
                            className={`px-4 py-2 font-medium ${activeTab === 'progress'
                                ? 'text-mpTeal border-b-2 border-mpTeal'
                                : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('progress')}
                        >
                            Progress Reports ({progressReports.length})
                        </button>
                    </div>

                    {/* Report Generator Tab */}
                    {activeTab === 'generator' && (
                        <Formik
                            enableReinitialize
                            initialValues={{
                                startDate: startApi,
                                endDate: endApi,
                                selectedStudent: { value: -1, label: 'Please select...' },
                                selectedReportType: { value: -1, label: 'Please select...' },
                                quickDownload: false,
                                quickDownloadDate: formatDateApi(new Date())
                            }}
                            innerRef={formRef}
                            onSubmit={handleSubmit}
                        >
                            {formik => (
                                <>
                                    <h2>Generate Progress Report</h2>
                                    <p className="text-gray-500 text-sm mb-4">
                                        Generate a PDF progress report for a student based on their appointments and completed lessons.
                                    </p>
                                    <br />
                                    <div className="flex flex-row gap-x-4 items-end flex-wrap">
                                        <div className="grid grid-cols-1 gap-y-2">
                                            <DateRangeSelector
                                                id="report-generator-manager-drs"
                                                startName="startDate"
                                                endName="endDate"
                                                startLabel="Start Date"
                                                endLabel="End Date"
                                                startValue={formik.values.startDate}
                                                endValue={formik.values.endDate}
                                                defaultValid={true}
                                                onStartChange={formik.handleChange}
                                                onEndChange={formik.handleChange}
                                                onChangeValidation={setDrsValid}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/4 min-w-[200px]">
                                            <SelectSingle
                                                id="report-generator-selectedStudent"
                                                name="selectedStudent"
                                                label="Student"
                                                value={formik.values.selectedStudent}
                                                onChange={formik.handleChange}
                                                options={studentOptions}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/4 min-w-[200px]">
                                            <SelectSingle
                                                id="report-generator-selectedReportType"
                                                name="selectedReportType"
                                                label="Report Type"
                                                value={formik.values.selectedReportType}
                                                onChange={formik.handleChange}
                                                options={reportTypeOptions}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2">
                                            <Button
                                                color="lte-mpTeal"
                                                onClick={formik.handleSubmit}
                                                disabled={formik.values.selectedStudent.value === -1 ||
                                                    formik.values.selectedReportType.value === -1}
                                            >
                                                Generate Report
                                            </Button>
                                        </div>
                                    </div>

                                    <br />

                                    <div className="flex flex-row gap-x-4 items-end">
                                        <div className="grid grid-cols-1 gap-y-2">
                                            <TooltipWrapper
                                                tooltipText="Download report without opening a popup window"
                                            >
                                                <Check
                                                    id="report-generator-quickDownload"
                                                    name="quickDownload"
                                                    color="mpLRed"
                                                    label={
                                                        <div className="text-mpLBlue">
                                                            Quick Download
                                                        </div>
                                                    }
                                                    checked={formik.values.quickDownload}
                                                    onChange={formik.handleChange}
                                                />
                                            </TooltipWrapper>
                                        </div>
                                        {formik.values.quickDownload ?
                                            <div className="grid grid-cols-1 gap-y-2">
                                                <DatePicker
                                                    id="report-generator-quickDownloadDate"
                                                    name="quickDownloadDate"
                                                    label="File Name Date"
                                                    value={formik.values.quickDownloadDate}
                                                    onChange={formik.handleChange}
                                                    disabled={loading}
                                                />
                                            </div> : null
                                        }
                                    </div>

                                    <br />

                                    {formik.status && !formik.isSubmitting ?
                                        <div className="text-mpLRed mr-8">
                                            {formik.status}
                                        </div> : null
                                    }
                                    {apiError ? <div className="text-mpLRed">{apiError}</div> : null}
                                </>
                            )}
                        </Formik>
                    )}

                    {/* Monthly Progress Reports Tab */}
                    {activeTab === 'progress' && (
                        <>
                            <h2>Progress Reports</h2>
                            <p className="text-gray-500 text-sm mb-4">
                                Manage Google Drive links and notes for student progress reports.
                            </p>

                            {/* Filters */}
                            <div className="flex flex-row gap-x-4 items-end flex-wrap mb-4">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-mpTeal"
                                        placeholder="Search by student name or notes..."
                                        value={filterQuery}
                                        onChange={(e) => setFilterQuery(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-x-4">
                                    <Check
                                        id="progress-showSentOnly"
                                        name="showSentOnly"
                                        label="Sent Only"
                                        color="mpTeal"
                                        checked={showSentOnly}
                                        onChange={() => {
                                            setShowSentOnly(!showSentOnly);
                                            if (!showSentOnly) setShowNotSentOnly(false);
                                        }}
                                    />
                                    <Check
                                        id="progress-showNotSentOnly"
                                        name="showNotSentOnly"
                                        label="Not Sent Only"
                                        color="mpYellow"
                                        checked={showNotSentOnly}
                                        onChange={() => {
                                            setShowNotSentOnly(!showNotSentOnly);
                                            if (!showNotSentOnly) setShowSentOnly(false);
                                        }}
                                    />
                                    <Check
                                        id="progress-showWithLinksOnly"
                                        name="showWithLinksOnly"
                                        label="With Links Only"
                                        color="mpLBlue"
                                        checked={showWithLinksOnly}
                                        onChange={() => setShowWithLinksOnly(!showWithLinksOnly)}
                                    />
                                </div>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={handleExportCSV}
                                >
                                    Export to CSV
                                </Button>
                            </div>

                            {/* Stats */}
                            <div className="flex gap-x-6 mb-4 text-sm">
                                <span className="text-gray-600">
                                    Total: <strong>{progressReports.length}</strong>
                                </span>
                                <span className="text-green-600">
                                    Sent: <strong>{progressReports.filter(r => r.reportSent === 1).length}</strong>
                                </span>
                                <span className="text-yellow-600">
                                    Not Sent: <strong>{progressReports.filter(r => r.reportSent !== 1).length}</strong>
                                </span>
                                <span className="text-blue-600">
                                    With Links: <strong>{progressReports.filter(r => r.reportLink && r.reportLink.trim()).length}</strong>
                                </span>
                                <span className="text-gray-500">
                                    Showing: <strong>{filteredProgressReports.length}</strong>
                                </span>
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto">
                                <Table
                                    tableName="progressReports"
                                    columns={columns}
                                    data={filteredProgressReports}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No progress reports found"
                                />
                            </div>

                            {apiError ? <div className="text-mpLRed mt-4">{apiError}</div> : null}
                        </>
                    )}
                </div>
            }
            <Socket
                refreshData={() => null}
                page={pageTitle}
                setVersion={props.setVersion}
            />
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
};

export default connect(mapStateToProps, {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchStudentsAll,
    fetchAppointmentsDaterangeStudent,
    fetchLearningPlanItemsStudentDaterangeCompleted,
    fetchLessonsAll,
    updateStudentAttributes
})(ReportGenerator);
